import { Component, EventEmitter, Input, Optional, Output, Self, OnChanges, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { OptionType } from './disney-dropdown.details';
import * as _ from 'lodash-es';
// tslint:disable:max-file-line-count
/**
 * selectedOption : For normal dropdowns if list is array of strings then it should be string
 * For non normal dropdowns (List is Array of objects)  selectedOption is also array of objects
 */
@Component({
  selector: 'dtr-hub-disney-dropdown',
  templateUrl: './disney-dropdown.component.html',
  styleUrls: ['./disney-dropdown.component.scss']
})
export class DisneyDropdownComponent implements ControlValueAccessor, OnChanges {
  @Input() listData: any[] = [];
  @Input() placeholder = 'Select';
  @Input() isMultiSelect = false;
  @Input() disabled = false;
  @Input() isBorderedView = true;
  @Input() isSearchable = true;
  @Input() optionType: OptionType = { key: 'id', name: 'name' };

  @Output() selectionChange: EventEmitter<any> = new EventEmitter();

  @Input() placeholderAsOption: any;
  @Input() errorMsgObj: any;
  @Input() hasFilter = false;
  @Input() hasLabels = false;
  @Input() selectedOption = null;
  @Input() isOptionSelected = false;
  @Input() isVirtual = false;
  @Input() defaultSelectionOption = null;
  @Input() allowInvertSelect = false;
  @Input() customSelectHeight: number;
  @Input() maxBottomScreenSpace = 16;
  @Input() limitSelection = false;
  @Input() maxSelection = 10;
  @ViewChild('formField') formField: ElementRef;
  @ViewChild('inputField') inputField: ElementRef;

  filterText: string;
  searchText = '';
  originalListData: any[] = [];
  placeholderAsOptionKey = -999999;
  showOptionsList = false;
  isError = false;
  selectAllObj: any;
  normalDropdown = false; // dropdown which accepts only arrays and not array of objects as listData
  invertSelect = false;

  constructor(@Self() @Optional() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.listData) {
      this.originalListData = _.cloneDeep(this.listData);
    }
  }

  getSelectSize() {
    const maxHeight = Math.ceil(Math.max(document.documentElement.clientHeight, window.innerHeight || 0) * 0.50);
    const searchSize = this.hasFilter ? 70 : 0;
    const itemSize = 44 * this.listData.length + searchSize;

    let finalSize = itemSize > maxHeight ? maxHeight : itemSize;

    if (this.customSelectHeight) {
      finalSize = finalSize > this.customSelectHeight ? this.customSelectHeight : finalSize;
    }

    return `${ finalSize + 1 }px`;
  }

  getViewportSize() {
    const searchSize = this.hasFilter ? 70 : 0;

    return `calc(100% - ${searchSize}px)`;
  }

  writeValue(val: any): void {
    this.selectedOption = val;
    this.isOptionSelected = this.checkIfOptionSelected();
    this.changeStepperEvent(this.value);
    this.setInitialData();
    this.setDisplayText();
  }

  setPlaceHolderAsOption() {
    if (this.placeholderAsOption) {

      this.listData = this.listData || [];
      if (this.normalDropdown) {
        if (this.listData[0] !== this.placeholderAsOption) {
          this.listData.unshift(this.placeholderAsOption);
        }
      } else {
        const defaultOption = {};
        defaultOption[this.optionType.key] = this.placeholderAsOptionKey;
        defaultOption[this.optionType.name] = this.placeholderAsOption;
        if (this.listData[0][this.optionType.key] !== this.placeholderAsOptionKey) {
          this.listData.unshift(defaultOption);
        }
      }

    }
  }

  setDropdownOptions() {
    if (this.selectedOption !== null && this.selectedOption !== undefined && !this.normalDropdown) {
      if (this.isMultiSelect) {
        this.listData.forEach((item, index) => {
          const isItemSelected = (this.selectedOption && this.selectedOption
            .find(option => item[this.optionType.key] === option[this.optionType.key]));
          this.listData[index] = {
            [this.optionType.key]: item[this.optionType.key],
            [this.optionType.name]: item[this.optionType.name], 'checked': isItemSelected ? true : false
          };
        });
      } else {
        if (this.listData) {
          this.listData.forEach((item) => {
            item.checked = false;
          });
        }
        const foundIndex = this.listData && this.listData
          .findIndex(option => this.selectedOption[this.optionType.key] === option[this.optionType.key]);
        if (foundIndex >= 0) {
          const item = this.listData[foundIndex];
          this.listData[foundIndex] = Object.assign({}, {
            [this.optionType.key]: item[this.optionType.key],
            [this.optionType.name]: item[this.optionType.name], 'checked': true
          });
        }
      }
    }
  }

  private setInitialData() {
    if (this.listData && this.listData.length) {
      const type = typeof this.listData[0];
      this.normalDropdown = false;
      switch (type) {
        case 'string':
        case 'number':
        case 'boolean':
          this.normalDropdown = true;
          break;
        case 'object':
          this.normalDropdown = false;
          break;
      }

      this.listData.sort((a, b) => {
        if (this.normalDropdown) { return a - b;
        } else {
          return (a[this.optionType.name] - b[this.optionType.name]);
        }
      });

      this.setPlaceHolderAsOption();
      this.setDropdownOptions();

      if (this.isMultiSelect) {
        this.addSelectAllOptionToListData();
      }
    }
  }

  // For isMultiSelect only
  private addSelectAllOptionToListData() {
    if (this.normalDropdown) {
      const isPresent = this.listData.indexOf('Select All');
      if (isPresent < 0) { this.listData.splice(0, 0, 'Select All'); }
    } else {
      const obj = { [this.optionType.key]: 'selectAll', [this.optionType.name]: 'Select All', checked: false };
      const isPresent = this.listData.find(data => data[this.optionType.name] === 'Select All');
      if (!isPresent) { this.listData.splice(0, 0, obj); }
      if (this.listData && this.selectedOption && (this.listData.length === (this.selectedOption.length + 1))) {
        this.listData[0].checked = true;
      }
    }
  }

  private checkIfOptionSelected() {

    const checkIfPlaceHolderSelected = () => {
      if (this.placeholderAsOption) {
        if (this.normalDropdown && this.selectedOption === this.placeholderAsOption) {
          return true;
        }
        if (!this.normalDropdown && this.selectedOption && (this.selectedOption[this.optionType.key] === this.placeholderAsOptionKey)) {
          return true;
        }
      }

      return false;
    };

    const forOptionTypeObject = () => {
      return typeof this.selectedOption === 'object' && (this.selectedOption !== null && this.selectedOption !== undefined);
    };

    const forOptionTypeString = () => {
      return typeof this.selectedOption === 'string' && this.selectedOption.length;
    };

    if (checkIfPlaceHolderSelected()) {
      return false;
    }
    if (forOptionTypeObject()) {
      return Object.keys(this.selectedOption).length ? true : false;
    }
    if (forOptionTypeString()) {
      return true;
    }
    if (this.selectedOption || typeof this.selectedOption === 'number' || typeof this.selectedOption === 'boolean') {
      return true;
    }

    return false;
  }

  showOption(event: PointerEvent) {
    const wHeight = window.innerHeight;
    const controlSize = 50;
    const bottomPosition = controlSize + event.clientY + Number(this.getSelectSize().replace('px', ''));
    const spaceToBottom = wHeight - bottomPosition;
    this.invertSelect = spaceToBottom < this.maxBottomScreenSpace;
    this.showOptionsList = !this.showOptionsList;
  }

  singleOptionSelection(selectedValue: any, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const hasSelectionOptionChanged = this.hasSelectOptionChanged(selectedValue);
    this.selectedOption = hasSelectionOptionChanged ? selectedValue :
      (this.defaultSelectionOption ? this.defaultSelectionOption : null);
    this.showOptionsList = false;
    if (!this.normalDropdown) {
      this.listData.forEach(
        item => item.checked = hasSelectionOptionChanged ?
          item[this.optionType.key] === selectedValue[this.optionType.key] :
          ((item[this.optionType.key] === selectedValue[this.optionType.key]) ?
            false : item.checked)
      );
      if (!this.listData.find(item => item.checked) && this.defaultSelectionOption) {
        this.listData.forEach(
          item => item.checked = item[this.optionType.key] === this.defaultSelectionOption[this.optionType.key]
        );
      }
    }
    this.isOptionSelected = this.checkIfOptionSelected();
    if (!this.isOptionSelected) {
      this.selectedOption = null;
    }
    this.setDisplayText();
    this.changeStepperEvent(this.selectedOption);
    this.selectionChange.emit(this.selectedOption);
  }

  setDisplayText() {
    if (this.isSearchable) {
      if (this.normalDropdown) {
        this.searchText = this.selectedOption ? this.selectedOption : '';
      } else {
        this.searchText = (this.selectedOption && this.selectedOption[this.optionType.name]) ?this.selectedOption[this.optionType.name] : '';
      }
    }
  }

  hasSelectOptionChanged(selectedValue: any) {
    return (this.selectedOption === null || this.selectedOption === undefined ||
    (this.normalDropdown && selectedValue !== this.selectedOption) ||
    (!this.normalDropdown && selectedValue[this.optionType.key] !== this.selectedOption[this.optionType.key]));
  }

  multiOptionSelection(item: any, event: any) {
    event.event.preventDefault();

    if (this.normalDropdown) {
      this.handleSelectionOfSimpleDropdown(item, event);
    } else {
     this.handleSelectionOfComplexDropdown(item, event);
    }

    this.isOptionSelected = this.checkIfOptionSelected();
    this.changeStepperEvent(this.selectedOption);
    this.selectionChange.emit(this.selectedOption);
  }

  private handleSelectionOfSimpleDropdown(item, event) {
    if (item === 'Select All') {
      if (event.checked) {
        this.selectedOption = [];
        this.listData.forEach(x => {
          if (x !== 'Select All') { this.selectedOption.push(x); }
        });
      } else {
        this.selectedOption = [];
      }
    } else {
      if (this.selectedOption === null || this.selectedOption === undefined) { this.selectedOption = []; }
      if (event.checked) {
        this.selectedOption.push(item);
      } else {
        const index = this.selectedOption.indexOf(item);
        if (index >= 0) { this.selectedOption.splice(index, 1); }
      }
    }
  }

  private handleSelectionOfComplexDropdown(item, event) {
    if (item[this.optionType.name] === 'Select All') {
      if (event.checked) {
        this.selectedOption = [];
        this.listData.forEach(x => {
          x['checked'] = event.checked;
          if (x[this.optionType.name] !== 'Select All') {
            this.selectedOption.push({ [this.optionType.key]: x[this.optionType.key], [this.optionType.name]: x[this.optionType.name]});
          }
        });
      } else {
        this.listData.forEach(x => { x['checked'] = event.checked; });
        this.selectedOption = [];
      }
    } else {
      if (this.selectedOption === null || this.selectedOption === undefined) { this.selectedOption = []; }
      const index = this.selectedOption.findIndex(
        option => option[this.optionType.key] === item[this.optionType.key] && option[this.optionType.name] === item[this.optionType.name]
      );
      if (index >= 0) {
        this.selectedOption.splice(index, 1);
      } else {
        this.selectedOption.push({ [this.optionType.key]: item[this.optionType.key], [this.optionType.name]: item[this.optionType.name]});
      }
      item['checked'] = index < 0;
      if (this.listData[0][this.optionType.name] === 'Select All') {
        this.listData[0]['checked'] = this.listData.length === (this.selectedOption.length + 1);
      }
    }
    
    this.listData.forEach(d => {
      const originalVal = this.originalListData.find(
        o => o[this.optionType.key] === d[this.optionType.key]
      );
      if(originalVal) { originalVal['checked'] = d['checked']; }
    });
  }

  clickedOutside() {
    this.showOptionsList = false;
    this.setActive(false);
    if (!this.selectedOption) {
      this.searchText = '';
      this.listData = _.cloneDeep(this.originalListData);
    }
  }

  resetSearch() {
    this.searchText = '';
    this.listData = _.cloneDeep(this.originalListData);
    this.showOptionsList = false;
    this.setActive(false);
    this.writeValue('');
    this.selectionChange.emit('');
  }

  registerOnChange(fn: any): void {
    this.changeStepperEvent = fn;
  }

  registerOnTouched(fn: any): void {
    this.onControlTouched = fn;
  }

  changeStepperEvent(val: any) {
    if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(val);
    }
  }

  onControlTouched(event: any) {}

  get invalid(): boolean {
    return this.ngControl ? this.ngControl.invalid : false;
  }

  get value() {
    return this.selectedOption;
  }

  get showError(): boolean {
    if (!this.ngControl) { return false; }
    const { dirty, touched } = this.ngControl;

    return (this.invalid) ? (dirty || touched) : false;
  }

  get errors(): Array<string> {
    if (!this.ngControl) { return []; }
    let { errors } = this.ngControl;

    if (this.errorMsgObj) {
      return Object.keys(errors).map(key => this.errorMsgObj[key] ? this.errorMsgObj[key] : <string>errors[key] || key);
    }

    return Object.keys(errors).map(key => <string>errors[key] || key);
  }

  filter(event: any) {
    if (this.isSearchable) { this.filterText = event.target.value ? event.target.value : ''; }
    if (this.filterText.trim() === '') {
      this.listData = _.cloneDeep(this.originalListData);
    } else {
      this.listData = _.cloneDeep(this.originalListData.filter(
          (data) => {
            if (data.isLabel) { return true; }
            const item = this.normalDropdown ? data : data[this.optionType.name];

            return String(item).toLowerCase().includes(this.filterText.toLowerCase());
          }
        )
      );
    }
  }

  setActive(active: boolean) {
    if (this.disabled) { return; }
    if (this.formField && this.formField.nativeElement) {
      const formField = this.formField.nativeElement;
      if (active) { formField.classList.add('form-field--is-active'); this.inputField.nativeElement.focus(); } else {
        formField.classList.remove('form-field--is-active');
      }
    }
  }

  isChecked(object: any) {
    if (typeof object === 'object' && 'checked' in object) {
      return object.checked;
    }
    if (Array.isArray(this.selectedOption)) {
      return this.selectedOption.findIndex((data) => this.isEqual(data, object)) !== -1;
    }

    return this.isEqual(this.selectedOption, object);
  }

  isEqual(obj1: any, obj2: any) {
    if (typeof obj1 === 'string') {
      return obj1 === obj2;
    }
    if (obj1 && obj2) {
      return obj1[this.optionType.key] === obj2[this.optionType.key]
          && obj1[this.optionType.name] === obj2[this.optionType.name];
    }

    return false;
  }
}